export interface PieSlice {
  label: string;
  value: number;
  color: string;
}

const PALETTE = [
  "#2563eb", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6",
  "#06b6d4", "#84cc16", "#f97316", "#ec4899", "#14b8a6",
];

export function renderPieChart(
  canvas: HTMLCanvasElement,
  slices: PieSlice[],
  opts: { title?: string; donut?: boolean } = {},
): void {
  // Sync canvas buffer to its CSS display size so the chart is never
  // stretched or blurry on wide/HiDPI screens. clientWidth/clientHeight give
  // the actual rendered CSS dimensions; fall back to attribute values when
  // the element is not yet in the layout (e.g. during unit tests).
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const cssW = canvas.clientWidth || canvas.width;
  const cssH = canvas.clientHeight || canvas.height;
  canvas.width = Math.round(cssW * dpr);
  canvas.height = Math.round(cssH * dpr);

  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  ctx.scale(dpr, dpr);

  const w = cssW;
  const h = cssH;
  ctx.clearRect(0, 0, w, h);

  const total = slices.reduce((s, sl) => s + sl.value, 0);
  if (!total || !slices.length) {
    ctx.fillStyle = "#94a3b8";
    ctx.font = "12px IBM Plex Sans";
    ctx.fillText("No data", 16, h / 2);
    return;
  }

  // Chart area (left portion for pie, right portion for legend)
  const cx = w * 0.3;
  const cy = h * 0.5;
  const radius = Math.min(cx - 10, cy - 20);
  const innerRadius = opts.donut ? radius * 0.55 : 0;

  let startAngle = -Math.PI / 2;
  for (const slice of slices) {
    const sliceAngle = (slice.value / total) * Math.PI * 2;
    ctx.beginPath();
    ctx.moveTo(cx + innerRadius * Math.cos(startAngle), cy + innerRadius * Math.sin(startAngle));
    ctx.arc(cx, cy, radius, startAngle, startAngle + sliceAngle);
    ctx.arc(cx, cy, innerRadius, startAngle + sliceAngle, startAngle, true);
    ctx.closePath();
    ctx.fillStyle = slice.color;
    ctx.fill();
    startAngle += sliceAngle;
  }

  // Title
  if (opts.title) {
    ctx.fillStyle = "#e2e8f0";
    ctx.font = "bold 12px IBM Plex Sans";
    ctx.fillText(opts.title, 8, 16);
  }

  // Legend (right side)
  const legendX = w * 0.6;
  let legendY = 24;
  ctx.font = "11px IBM Plex Sans";
  for (const slice of slices.slice(0, 8)) {
    const pct = Math.round((slice.value / total) * 100);
    ctx.fillStyle = slice.color;
    ctx.fillRect(legendX, legendY - 8, 10, 10);
    ctx.fillStyle = "#cbd5e1";
    ctx.fillText(`${slice.label} (${pct}%)`, legendX + 14, legendY);
    legendY += 16;
  }
}

export function assignColors(labels: string[]): PieSlice[] {
  return labels.map((label, i) => ({
    label,
    value: 0,
    color: PALETTE[i % PALETTE.length],
  }));
}
